import React, { useState, useEffect } from 'react'
import { toast } from '../../components/toast/CustomToast'
import api from '../../utils/api'
import TraceStats from '../../components/company/TraceStats'
import TraceDetailModal from '../../components/company/TraceDetailModal'
import Pagination from '../../components/common/Pagination'
import { getPeriodDateRange } from '../../utils/statsPeriod'
import ViewingCompanyChip from '../../components/company/ViewingCompanyChip'

const PAGE_SIZE = 10

// companyId: whose traces to show - the logged company by default, or an associated company when a núcleo
// drills into one (viewedCompany is then set, so the header says whose events these are).
const EventsTracePage = ({ integration, onBack, initialPeriod, onOpenErrorsPage, companyId: companyIdProp, viewedCompany }) => {
    const [traces, setTraces] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadingStats, setLoadingStats] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [selectedTrace, setSelectedTrace] = useState(null)
    const [selectedTraceIds, setSelectedTraceIds] = useState(new Set())
    const [expandedErrorIds, setExpandedErrorIds] = useState(new Set())
    const [reprocessing, setReprocessing] = useState(false)

    const [statusFilter, setStatusFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [pathFilter, setPathFilter] = useState('')
    const [schemaFields, setSchemaFields] = useState([])
    const [loadingSchema, setLoadingSchema] = useState(false)
    const [isPathDropdownOpen, setIsPathDropdownOpen] = useState(false)
    const [pathSearchQuery, setPathSearchQuery] = useState('')

    const todayStr = () => new Date().toISOString().split('T')[0]
    const sevenDaysAgoStr = () => {
        const d = new Date()
        d.setDate(d.getDate() - 6)
        return d.toISOString().split('T')[0]
    }

    // Opening the traces page from the dashboard carries over whichever period
    // (Hoje/Ontem/7 dias) was selected there, so the trace list matches what
    // the user was just looking at. Falls back to the original 7-day window
    // when no period is passed (e.g. opened from the integrations list).
    const getDefaultDateRange = () => {
        const { dateFrom: from, dateTo: to } = getPeriodDateRange(initialPeriod || '7d')
        return { dateFrom: from || sevenDaysAgoStr(), dateTo: to || todayStr() }
    }

    const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange().dateFrom)
    const [dateTo, setDateTo] = useState(() => getDefaultDateRange().dateTo)

    // Snapshot of the filters actually applied on the server for the currently
    // loaded page - distinct from the draft values in the inputs above, which
    // only take effect once the user clicks "Atualizar". Pagination and any
    // reload that shouldn't reset filters (reprocess, etc.) reuse this as-is.
    const [appliedFilters, setAppliedFilters] = useState(() => ({
        status: 'all',
        ...getDefaultDateRange(),
        path: null,
        search: null,
    }))

    const companyId = companyIdProp || JSON.parse(localStorage.getItem('user_info') || '{}').company_id

    const loadSchema = async (dateFromParam, dateToParam) => {
        if (!integration?.id) {
            console.log('loadSchema: skipping - no integration.id')
            return
        }
        setLoadingSchema(true)
        try {
            const params = { integration_id: integration.id }
            if (companyId) {
                params.company_id = companyId
            }
            const fromDate = dateFromParam || dateFrom
            const toDate = dateToParam || dateTo
            if (fromDate) params.date_from = fromDate
            if (toDate) params.date_to = toDate

            const response = await api.get('/api/data-filters/schema', {
                params
            })
            const fields = response.data.fields || []
            const dateTimePattern = /(date|time|datetime)/i
            const filteredFields = fields
                .filter(f => f.type !== 'null')
                .filter(f => !dateTimePattern.test(f.path))
                .sort((a, b) => a.path.localeCompare(b.path))
            setSchemaFields(filteredFields)
        } catch (error) {
            console.error('Error loading schema:', error)
            setSchemaFields([])
        } finally {
            setLoadingSchema(false)
        }
    }

    const filteredSchemaFields = schemaFields.filter(field =>
        (field.label || field.path).toLowerCase().includes(pathSearchQuery.toLowerCase()) ||
        field.path.toLowerCase().includes(pathSearchQuery.toLowerCase())
    )


    const handleSearchChange = (value) => {
        setSearchQuery(value)
    }

    useEffect(() => {
        if (companyId && integration) {
            const initialFilters = {
                status: 'all',
                ...getDefaultDateRange(),
                path: null,
                search: null,
            }
            loadStats(initialFilters)
            loadTraces(initialFilters, 1)
            loadSchema()
        }
    }, [companyId, integration, initialPeriod])

    const loadStats = async (filters) => {
        setLoadingStats(true)
        try {
            const params = {}
            if (filters.dateFrom) params.date_from = filters.dateFrom
            if (filters.dateTo) params.date_to = filters.dateTo
            if (filters.path && filters.search) {
                params.path = filters.path
                params.value = filters.search
            } else if (filters.search) {
                params.search = filters.search
            }

            const response = await api.get(
                `/api/event-traces/company/${companyId}/integration/${integration.id}/stats`,
                { params }
            )
            setStats(response.data)
        } catch (error) {
            console.error('Error loading stats:', error)
        } finally {
            setLoadingStats(false)
        }
    }

    const loadTraces = async (filters, targetPage) => {
        setLoading(true)
        try {
            const params = { page: targetPage, page_size: PAGE_SIZE }
            if (filters.status && filters.status !== 'all') params.status = filters.status
            if (filters.dateFrom) params.date_from = filters.dateFrom
            if (filters.dateTo) params.date_to = filters.dateTo
            if (filters.path && filters.search) {
                params.path = filters.path
                params.value = filters.search
            } else if (filters.search) {
                params.search = filters.search
            }

            const response = await api.get(
                `/api/event-traces/company/${companyId}/integration/${integration.id}`,
                { params }
            )
            setTraces(response.data.items)
            setTotal(response.data.total)
            setPage(targetPage)
            setAppliedFilters(filters)
            setSelectedTraceIds(new Set())
        } catch (error) {
            console.error('Error loading traces:', error)
            toast.error('Erro ao carregar traces')
        } finally {
            setLoading(false)
        }
    }

    const handleRefresh = async () => {
        setRefreshing(true)
        try {
            // Reload schema with current date range
            await loadSchema(dateFrom, dateTo)
            const nextFilters = {
                status: statusFilter,
                dateFrom,
                dateTo,
                path: pathFilter || null,
                search: searchQuery || null,
            }
            await Promise.all([
                loadStats(nextFilters),
                loadTraces(nextFilters, 1)
            ])
        } finally {
            setRefreshing(false)
        }
    }

    // Opens the same incidence-grouping screen used from the dashboard's
    // "Falha" card, scoped to just this integration. IntegrationsErrorsPage
    // groups by error signature from a `stats` snapshot shaped like the
    // dashboard's per-integration daily rollup - this page only has the
    // aggregate EventTraceStatsResponse (completed/failed totals for the
    // applied date range), so a single synthetic "daily" bucket is enough to
    // carry the failed count through; the integration's own logo/supplier
    // are resolved downstream from the company's full integrations list.
    const handleOpenIncidencePage = () => {
        if (!onOpenErrorsPage || !integration?.id) return
        const statSnapshot = [{
            integration_id: integration.id,
            integration_name: integration.name,
            supplier_name: integration.supplier?.name,
            daily: [{ date: dateFrom, success: stats?.completed || 0, error: stats?.failed || 0 }]
        }]
        onOpenErrorsPage(statSnapshot, initialPeriod || '7d')
    }

    const handleReprocess = async (trace) => {
        try {
            const response = await api.post('/api/cloud-event-logs/reprocess', {
                event_ids: [trace.cloud_event_id],
                trace_ids: [trace.id]
            })
            toast.success(response.data.message || 'Evento reenfileirado para reprocessamento')
            loadTraces(appliedFilters, page)
            loadStats(appliedFilters)
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao reprocessar evento')
            throw error
        }
    }

    const handleBulkReprocess = async () => {
        if (selectedTraceIds.size === 0) return
        setReprocessing(true)
        try {
            const selected = traces.filter(t => selectedTraceIds.has(t.id))
            const eventIds = selected.map(t => t.cloud_event_id)
            const traceIds = selected.map(t => t.id)
            const response = await api.post('/api/cloud-event-logs/reprocess', {
                event_ids: eventIds,
                trace_ids: traceIds
            })
            toast.success(response.data.message || 'Eventos reenfileirados para reprocessamento')
            setSelectedTraceIds(new Set())
            loadTraces(appliedFilters, page)
            loadStats(appliedFilters)
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao reprocessar eventos')
        } finally {
            setReprocessing(false)
        }
    }

    const toggleTraceSelection = (id) => {
        const next = new Set(selectedTraceIds)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        setSelectedTraceIds(next)
    }

    const toggleErrorExpanded = (id) => {
        const next = new Set(expandedErrorIds)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        setExpandedErrorIds(next)
    }

    const toggleAllVisible = () => {
        const allSelected = traces.every(t => selectedTraceIds.has(t.id))
        const next = new Set(selectedTraceIds)
        if (allSelected) {
            traces.forEach(t => next.delete(t.id))
        } else {
            traces.forEach(t => next.add(t.id))
        }
        setSelectedTraceIds(next)
    }

    const getTraceStatus = (trace) => {
        const hasFailed = trace.steps.some(s => s.status === 'failed')
        // log_status is the CloudEventLog row correlated to this trace (backend-computed) -
        // it's the pipeline's own source of truth, so it keeps this consistent with the
        // dashboard card's "Processando" count even when step data is missing/incomplete
        // (e.g. a tracer hiccup on the worker - see jobs/cloudevent_processor.py).
        const logStillRunning = trace.log_status === 'processing' || trace.log_status === 'pending'
        const hasRunning = trace.steps.some(s => s.status === 'running') || logStillRunning
        const allCompleted = trace.steps.length > 0 && trace.steps.every(s => s.status === 'completed' || s.status === 'skipped')
        if (trace.reprocessed) return 'reprocessed_failed'
        if (hasFailed) return 'failed'
        if (hasRunning) return 'running'
        if (allCompleted) return 'completed'
        return 'pending'
    }

    const getTraceError = (trace) => {
        const failedStep = trace.steps.find(s => s.status === 'failed' && s.error_message)
        if (!failedStep?.error_message) return null
        const raw = String(failedStep.error_message).replaceAll('NoneType: None', '').trim()
        return raw || null
    }

    const formatTraceError = (rawError) => {
        if (!rawError) return null
        const raw = String(rawError).trim()

        const extractMessage = (parsed) => {
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.detail || parsed.message)) {
                return String(parsed.detail || parsed.message)
            }
            return null
        }

        // Tenta JSON válido primeiro
        try {
            //const fromJson = extractMessage(JSON.parse(raw))
            const fromJson = raw
            if (fromJson) return fromJson
        } catch (e) {
            // não é JSON válido, tenta como dict Python (aspas simples, None/True/False)
        }

        try {
            const pythonLikeAsJson = raw
                .replace(/\bNone\b/g, 'null')
                .replace(/\bTrue\b/g, 'true')
                .replace(/\bFalse\b/g, 'false')
                .replace(/'/g, '"')
            const fromPythonDict = extractMessage(JSON.parse(pythonLikeAsJson))
            if (fromPythonDict) return fromPythonDict
        } catch (e) {
            // ainda não deu, tenta extrair via regex como último recurso
        }

        const match = raw.match(/['"]message['"]\s*:\s*['"]([^'"]*)['"]/)
        if (match) return match[1]

        return raw
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed': return {
                label: 'Concluído',   icon: 'check_circle',
                class:       'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
                stripeClass: 'border-l-green-500 dark:border-l-green-400',
                dotClass:    'text-green-400 dark:text-green-600',
                codeClass:   'text-gray-600 dark:text-gray-300',
                copyClass:   'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200',
                dateClass:   'text-gray-500 dark:text-gray-400',
            }
            case 'failed': return {
                label: 'Falha',       icon: 'error',
                class:       'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
                stripeClass: 'border-l-red-500 dark:border-l-red-400',
                dotClass:    'text-gray-400 dark:text-gray-500',
                codeClass:   'text-gray-600 dark:text-gray-300',
                copyClass:   'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200',
                dateClass:   'text-gray-500 dark:text-gray-400',
                footerBg:     'bg-white dark:bg-gray-800',
                footerText:   'text-red-400/70 dark:text-red-400/60',
                footerIcon:   'text-red-300 hover:text-red-500 dark:text-red-400/60 dark:hover:text-red-300',
                footerDivider: 'bg-red-100 dark:bg-red-900/40',
            }
            case 'reprocessed_failed': return {
                label: 'Falha Reprocessada', icon: 'restart_alt',
                class:       'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
                stripeClass: 'border-l-amber-500 dark:border-l-amber-400',
                dotClass:    'text-gray-400 dark:text-gray-500',
                codeClass:   'text-gray-600 dark:text-gray-300',
                copyClass:   'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200',
                dateClass:   'text-gray-500 dark:text-gray-400',
                footerBg:     'bg-amber-50/50 dark:bg-amber-950/20',
                footerText:   'text-amber-600/80 dark:text-amber-400/70',
                footerIcon:   'text-amber-500 hover:text-amber-700 dark:hover:text-amber-300',
                footerDivider: 'bg-amber-200 dark:bg-amber-800/60',
            }
            case 'running': return {
                label: 'Processando', icon: 'autorenew',
                class:       'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
                stripeClass: 'border-l-blue-500 dark:border-l-blue-400',
                dotClass:    'text-gray-400 dark:text-gray-500',
                codeClass:   'text-gray-600 dark:text-gray-300',
                copyClass:   'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200',
                dateClass:   'text-gray-500 dark:text-gray-400',
            }
            default: return {
                label: 'Pendente',    icon: 'schedule',
                class:       'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
                stripeClass: 'border-l-gray-400 dark:border-l-gray-500',
                dotClass:    'text-gray-400 dark:text-gray-500',
                codeClass:   'text-gray-600 dark:text-gray-400',
                copyClass:   'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200',
                dateClass:   'text-gray-500 dark:text-gray-400',
            }
        }
    }

    const formatDuration = (ms) => {
        if (!ms) return '-'
        if (ms < 1000) return `${ms}ms`
        if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`
        return `${(ms / 60000).toFixed(2)}min`
    }

    // Filtering and pagination happen server-side (see loadTraces) - `traces` is
    // already just the current page's items.
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const allVisibleSelected = traces.length > 0 && traces.every(t => selectedTraceIds.has(t.id))

    const formatDate = (dateStr) => {
        const d = new Date(dateStr)
        const pad = (n) => String(n).padStart(2, '0')
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }

    const copyToClipboard = (text, e, successMessage = 'ID copiado!') => {
        e.stopPropagation()
        navigator.clipboard.writeText(text)
            .then(() => toast.success(successMessage))
            .catch(() => toast.error('Erro ao copiar'))
    }

    const { dateFrom: defaultDateFrom, dateTo: today } = getDefaultDateRange()
    const hasDateFilter = dateFrom !== defaultDateFrom || dateTo !== today

    const clearDateFilter = () => {
        setDateFrom(defaultDateFrom)
        setDateTo(today)
        setSelectedTraceIds(new Set())
        const nextFilters = { ...appliedFilters, dateFrom: defaultDateFrom, dateTo: today }
        loadStats(nextFilters)
        loadTraces(nextFilters, 1)
    }

    const handleFilterChange = (setter, value) => {
        setter(value)
        setSelectedTraceIds(new Set())
        if (setter === setStatusFilter) {
            loadTraces({ ...appliedFilters, status: value }, 1)
        }
    }

    const clearPathFilter = () => {
        setPathFilter('')
        setSearchQuery('')
        const nextFilters = { ...appliedFilters, path: null, search: null }
        loadStats(nextFilters)
        loadTraces(nextFilters, 1)
    }

    return (
        <div className="p-2 sm:p-3 h-[calc(100vh-90px)] flex flex-col">

            {/* Header: back arrow + logo card + title — all inline */}
            <div className="flex items-center gap-2 mb-0.5">
                {/* Back — icon only */}
                <button
                    onClick={onBack}
                    title="Voltar para integrações"
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white transition"
                >
                    <span className="material-icons" style={{ fontSize: '20px' }}>arrow_back</span>
                </button>

                {/* Integration logo + name card */}
                <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1 flex-shrink-0">
                    {integration.supplier?.logo_url ? (
                        <img
                            src={integration.supplier.logo_url}
                            alt={integration.supplier.name}
                            className="w-5 h-5 object-contain rounded"
                        />
                    ) : (
                        <span className="material-icons text-purple-500" style={{ fontSize: '18px' }}>timeline</span>
                    )}
                    <div className="leading-tight">
                        <p className="text-xs font-semibold text-gray-900 dark:text-white">{integration.name}</p>
                    </div>
                </div>

                {/* Page title */}
                <h1 className="text-base font-bold text-gray-900 dark:text-white">Traces</h1>
                <ViewingCompanyChip company={viewedCompany} />
            </div>

            {/* Two-column layout: main content | sidebar stats */}
            <div className="flex gap-6 flex-1 overflow-hidden">

                {/* Left — filters + list */}
                <div className="flex-1 min-w-0 flex flex-col gap-1 overflow-hidden">

                    {/* Filters — single row */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-1.5">
                        <div className="flex flex-wrap gap-1.5 items-center">
                            {/* Path Filter Dropdown */}
                            <div className="relative min-w-[180px]">
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setIsPathDropdownOpen(!isPathDropdownOpen)}
                                        disabled={loadingSchema || schemaFields.length === 0}
                                        className="flex-1 h-9 flex items-center gap-2 px-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white hover:border-gray-400 dark:hover:border-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                    >
                                        <span className="material-icons flex-shrink-0" style={{ fontSize: '16px' }}>
                                            {loadingSchema ? 'hourglass_empty' : 'tune'}
                                        </span>
                                        <span className="flex-1 truncate text-xs">
                                            {pathFilter ? (() => {
                                                const selected = schemaFields.find(f => f.path === pathFilter)
                                                return selected?.label || pathFilter
                                            })() : 'Filtro'}
                                        </span>
                                        <span className="material-icons flex-shrink-0" style={{ fontSize: '16px' }}>
                                            {isPathDropdownOpen ? 'expand_less' : 'expand_more'}
                                        </span>
                                    </button>
                                    {pathFilter && (
                                        <button
                                            onClick={clearPathFilter}
                                            title="Limpar filtro"
                                            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 transition"
                                        >
                                            <span className="material-icons" style={{ fontSize: '16px' }}>close</span>
                                        </button>
                                    )}
                                </div>

                                {isPathDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-1 z-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[600px]">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar atributo..."
                                            value={pathSearchQuery}
                                            onChange={(e) => setPathSearchQuery(e.target.value)}
                                            autoFocus
                                            className="w-full px-3 py-2 text-sm border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
                                        />
                                        <div className="max-h-64 overflow-y-auto">
                                            {filteredSchemaFields.length > 0 ? (
                                                filteredSchemaFields.map(fieldObj => (
                                                    <button
                                                        key={fieldObj.path}
                                                        onClick={() => {
                                                            setPathFilter(fieldObj.path)
                                                            setIsPathDropdownOpen(false)
                                                            setPathSearchQuery('')
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                                    >
                                                        <div className="flex flex-col gap-0.5">
                                                            <div className="font-medium">
                                                                {fieldObj.label || fieldObj.path}
                                                            </div>
                                                            {fieldObj.label && (
                                                                <div className="text-gray-500 dark:text-gray-400 text-[10px]">
                                                                    {fieldObj.path}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="px-3 py-2 text-xs text-gray-400 text-center">Nenhum atributo encontrado</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Search */}
                            <div className="relative flex-1 min-w-0">
                                <span className="material-icons absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: '14px' }}>search</span>
                                <input
                                    type="text"
                                    placeholder={pathFilter ? `Pesquisar em "${pathFilter}"...` : "Buscar por ID, tipo, source ou erro..."}
                                    value={searchQuery}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                    className="w-full h-8 pl-7 pr-2 py-0 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            {/* Date range — aplica ao clicar Atualizar */}
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                title="Data inicial (clique Atualizar para aplicar)"
                                className="flex-shrink-0 h-8 px-1.5 py-0 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-xs text-gray-400 flex-shrink-0 px-0.5 h-8 flex items-center">até</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                title="Data final (clique Atualizar para aplicar)"
                                className="flex-shrink-0 h-8 px-1.5 py-0 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                            />
                            {hasDateFilter && (
                                <button
                                    onClick={clearDateFilter}
                                    title="Limpar filtro de data"
                                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition"
                                >
                                    <span className="material-icons" style={{ fontSize: '15px' }}>close</span>
                                </button>
                            )}
                            {/* Refresh */}
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing || loading}
                                title="Atualizar lista"
                                className="h-8 flex items-center gap-1 px-2 py-0 text-xs font-medium bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                            >
                                <span className={`material-icons ${refreshing ? 'animate-spin' : ''}`} style={{ fontSize: '16px' }}>refresh</span>
                                <span className="hidden sm:inline">Atualizar</span>
                            </button>
                        </div>
                    </div>

                    {/* Inactive integration warning */}
                    {!integration?.active && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded text-sm">
                            <span className="material-icons text-amber-500 dark:text-amber-400 flex-shrink-0" style={{ fontSize: '18px' }}>warning</span>
                            <p className="font-medium text-amber-800 dark:text-amber-200">
                                Integração <strong>inativa</strong> - reprocessamento desabilitado
                            </p>
                        </div>
                    )}

                    {/* Bulk reprocess banner */}
                    {selectedTraceIds.size > 0 && integration?.active && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded px-3 py-2 flex items-center justify-between text-sm">
                            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
                                {selectedTraceIds.size} trace(s) selecionado(s)
                            </p>
                            <button
                                onClick={handleBulkReprocess}
                                disabled={reprocessing}
                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white rounded-lg text-sm font-medium transition flex items-center gap-1.5"
                            >
                                <span className={`material-icons ${reprocessing ? 'animate-spin' : ''}`} style={{ fontSize: '14px' }}>
                                    {reprocessing ? 'sync' : 'refresh'}
                                </span>
                                {reprocessing ? 'Reprocessando...' : 'Reprocessar'}
                            </button>
                        </div>
                    )}

                    {/* Traces list */}
                    <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-center">
                                    <div className="animate-spin inline-block w-6 h-6 border-3 border-gray-200 border-t-blue-500 rounded-full mb-2"></div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Carregando traces...</p>
                                </div>
                            </div>
                        ) : traces.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                                <div className="text-center">
                                    <span className="material-icons mb-1 block" style={{ fontSize: '32px' }}>timeline</span>
                                    <p className="text-xs font-medium mb-0.5">Nenhum trace encontrado</p>
                                    <p className="text-xs opacity-75">Ajuste os filtros ou aguarde novos eventos</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Table */}
                                <div className="flex-1 overflow-auto w-full">
                                    <table className="w-full min-w-full">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                                                {integration?.active && (
                                                    <th className="px-2 py-1.5 text-left">
                                                        <input
                                                            type="checkbox"
                                                            checked={allVisibleSelected}
                                                            onChange={toggleAllVisible}
                                                            className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                                                        />
                                                    </th>
                                                )}
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Source</th>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Trace ID</th>
                                                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Data/Hora</th>
                                                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {traces.map((trace) => {
                                                const status = getTraceStatus(trace)
                                                const badge = getStatusBadge(status)
                                                const traceError = (status === 'failed' || status === 'reprocessed_failed')
                                                    ? formatTraceError(getTraceError(trace))
                                                    : null
                                                const colSpan = integration?.active ? 6 : 5
                                                const isErrorExpanded = Boolean(traceError) && expandedErrorIds.has(trace.id)
                                                return (
                                                <React.Fragment key={trace.id}>
                                                    <tr className={`border-b ${isErrorExpanded ? 'border-transparent' : 'border-gray-200 dark:border-gray-700'} hover:bg-gray-50 dark:hover:bg-gray-700/50 transition text-xs`}>
                                                        {integration?.active && (
                                                            <td className="px-2 py-3 text-left" onClick={(e) => e.stopPropagation()}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedTraceIds.has(trace.id)}
                                                                    onChange={() => toggleTraceSelection(trace.id)}
                                                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
                                                                />
                                                            </td>
                                                        )}
                                                        <td className="px-2 py-3 text-left">
                                                            <div className="flex items-center gap-1">
                                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${badge.class}`}>
                                                                    <span className="material-icons" style={{ fontSize: '12px' }}>{badge.icon}</span>
                                                                    {badge.label}
                                                                </span>
                                                                {traceError && (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); toggleErrorExpanded(trace.id) }}
                                                                        title={isErrorExpanded ? 'Ocultar detalhe do erro' : 'Ver detalhe do erro'}
                                                                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition"
                                                                    >
                                                                        <span className="material-icons" style={{ fontSize: '14px' }}>
                                                                            {isErrorExpanded ? 'expand_less' : 'expand_more'}
                                                                        </span>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-3 text-left">
                                                            <div className="flex flex-col">
                                                                <span className="text-gray-600 dark:text-gray-300 truncate max-w-xs">{trace.source}</span>
                                                                {trace.event_type && (
                                                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 truncate max-w-xs">{trace.event_type}</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-3 text-left">
                                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                                <code className={`font-mono text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-900/40 truncate max-w-[180px] ${badge.codeClass}`}>{trace.trace_id}</code>
                                                                <button
                                                                    onClick={(e) => copyToClipboard(trace.trace_id, e)}
                                                                    title="Copiar Trace ID"
                                                                    className={`flex-shrink-0 transition ${badge.copyClass}`}
                                                                >
                                                                    <span className="material-icons" style={{ fontSize: '12px' }}>content_copy</span>
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-3 text-left">
                                                            <span className={`font-mono ${badge.dateClass}`}>{formatDate(trace.created_at)}</span>
                                                        </td>
                                                        <td className="px-2 py-3 text-center">
                                                            <button
                                                                onClick={() => setSelectedTrace(trace)}
                                                                title="Ver detalhes"
                                                                className="inline-flex items-center justify-center gap-1 px-1.5 py-1 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 font-medium rounded transition"
                                                            >
                                                                <span className="material-icons flex-shrink-0" style={{ fontSize: '16px' }}>visibility</span>
                                                                <span className="hidden sm:inline">Detalhes</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {isErrorExpanded && (
                                                        <tr className={`border-b border-gray-200 dark:border-gray-700 ${badge.footerBg}`}>
                                                            <td colSpan={colSpan} className="px-2 py-2.5">
                                                                <div
                                                                    title={traceError}
                                                                    className={`flex items-center gap-2 text-[10px] leading-tight ${badge.footerText}`}
                                                                >
                                                                    <button
                                                                        onClick={(e) => copyToClipboard(traceError, e, 'Falha copiada!')}
                                                                        title="Copiar erro"
                                                                        className={`flex-shrink-0 transition ${badge.footerIcon}`}
                                                                    >
                                                                        <span className="material-icons" style={{ fontSize: '14px' }}>content_copy</span>
                                                                    </button>
                                                                    <span className={`w-px self-stretch flex-shrink-0 ${badge.footerDivider}`}></span>
                                                                    <span className="break-all flex-1">{traceError}</span>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer - Pagination */}
                                <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        totalItems={total}
                                        itemsPerPage={PAGE_SIZE}
                                        onPageChange={(p) => loadTraces(appliedFilters, p)}
                                    />
                                </div>

                            </>
                        )}
                    </div>
                </div>

                {/* Right sidebar — stats */}
                <div className="w-64 overflow-auto flex-shrink-0">
                    <TraceStats
                        stats={stats}
                        loading={loadingStats}
                        activeFilter={statusFilter}
                        onFilterChange={(f) => handleFilterChange(setStatusFilter, f)}
                        onOpenIncidencePage={handleOpenIncidencePage}
                    />
                </div>
            </div>

            {selectedTrace && (
                <TraceDetailModal
                    trace={selectedTrace}
                    onClose={() => setSelectedTrace(null)}
                    onReprocess={integration?.active ? handleReprocess : null}
                />
            )}
        </div>
    )
}

export default EventsTracePage
